import React, { useState, useEffect, useRef } from "react";
import {
  FaTrashAlt,
  FaSearch,
  FaCalendarAlt,
  FaArrowLeft,
  FaStar,
  FaEye,
} from "react-icons/fa";
import "./summaries.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { API_BASE } from "../apiConfig";
import Toast from "./Toast";

const Summaries = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [clients, setClients] = useState([]); // Start with empty array
  const [loading, setLoading] = useState(true); // Add loading state
  const [favoritesOnly, setFavoritesOnly] = useState(false); // filter to favourites only
  const navigate = useNavigate();
  const location = useLocation();
  const fromReview = new URLSearchParams(location.search).get("fromReview") === "1";
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const token = localStorage.getItem("token");

  // -- Appointment-type display label ---------------------------------------
  // get_summary.php returns the raw create_pathway label (lowercase, e.g.
  // "review smart goal"). Map it to the display labels used in the Session
  // column: "Review" reads as "Review to Set New Goal" and
  // "Review Smart Goal" reads as "Review Smart Goal Progress". Unknown
  // values fall back to title-casing.
  const appointmentLabelMap = {
    initial: "Initial",
    review: "Review to Set New Goal",
    "review smart goal": "Review Smart Goal Progress",
  };
  const appointmentDisplayLabel = (raw) => {
    const key = String(raw || "").trim().toLowerCase();
    if (appointmentLabelMap[key]) return appointmentLabelMap[key];
    return raw
      ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
      : "";
  };

  // -- Fetch Data from API ----------------------------------------
  const fetchSummaries = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/therapist_api/get_summary.php`,
        {
          method: "GET", // or "POST" depending on your API requirements
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await response.json();

      if (json.status && json.data) {
        // Every appointment type is listed here - Initial, Review AND
        // Review Smart Goal. This used to drop the Review Smart Goal rows, so
        // a client whose only suggested interventions came from a Review Smart
        // Goal appointment had no row at all on this page even though the
        // therapist had done the work. The Appointment Type column reads the
        // canonical label from appointmentDisplayLabel above, so those rows
        // show as "Review Smart Goal".

        // Map API data to the component's internal structure
        const mappedClients = json.data
          .map((item) => ({
            id: item.client_code || item.client_id || "",
            clientId: item.client_id || item.clientId || "",
            sessionId: item.sessionId || item.session_id || "",
            name: item.client_name || "",
            assessmentDate: item.created_at || "",
            appointmentType: item.appointment_type || "",
            is_favourite: item.is_favourite || false,
            goalDomain: item.goal_domain || "",
            targetDate: item.target_date || "",
            status: item.status || "",
            achievedBy: item.achieved_by || "",
            comments: item.comments || "",
          }))
          .sort(
            (a, b) => new Date(b.assessmentDate) - new Date(a.assessmentDate)
          );
        setClients(mappedClients);
      } else {
        console.error("API returned status false or no data");
        setClients([]);
      }
    } catch (error) {
      console.error("Error fetching summaries:", error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // -- Toggle favourite (per row) ---------------------------------------
  // The star in the Action column calls pathway_favorites.php with THAT
  // ROW's sessionId (the API is session-scoped, not client-scoped - same
  // call start-assessment's handleToggleFavourite makes). The server
  // response carries the authoritative is_favourite, so the row is updated
  // from it rather than blindly flipping the local flag - a failed toggle
  // leaves the star exactly as it was.
  const handleToggleFavourite = async (sessionId) => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${API_BASE}/therapist_api/pathway_favorites.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ sessionId: parseInt(sessionId), action: "toggle" })
      });
      const json = await response.json();
      if (json.status) {
        setClients((prev) =>
          prev.map((c) =>
            String(c.sessionId) === String(sessionId)
              ? { ...c, is_favourite: json.is_favourite === true }
              : c
          )
        );
        console.log(json.message);
      } else {
        alert("Failed to update favourite: " + (json.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Failed to toggle favourite:", error.message);
    }
  };

  // -- Helper Functions --------------------------------------------

  // 1. Add this state inside the component (near your other useState hooks)
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteClientIds, setDeleteClientIds] = useState([]);
  const [toast, setToast] = useState(null);

  // -- Multi-select (checkbox) state ------------------------------
  // The per-row selection checkboxes are not rendered, so this stays empty and
  // the bulk-delete bar below it is inert. Kept because handleDeleteSelected
  // reads it.
  const [selectedClientIds, setSelectedClientIds] = useState([]);

  // 2. Replace the existing handleDeleteClient function with this logic
  const handleDeleteClient = (clientId) => {
    // Open the popup and store which client we want to delete
    setDeleteClientIds([clientId]);
    setShowDeletePopup(true);
  };

  const handleDeleteSelected = () => {
    if (selectedClientIds.length === 0) return;
    setDeleteClientIds(selectedClientIds);
    setShowDeletePopup(true);
  };

  const confirmDelete = async () => {
    // Close popup immediately
    setShowDeletePopup(false);
    setToast(null);

    const token = localStorage.getItem("token");

    // Validate that we have client IDs to delete
    if (!deleteClientIds || deleteClientIds.length === 0) {
      setToast({ message: "No client selected for deletion.", type: "error" });
      return;
    }

    console.log("Deleting client IDs:", deleteClientIds);

    try {
      const response = await fetch(`${API_BASE}/therapist_api/delete_client.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          clientIds: deleteClientIds
        })
      });

      const result = await response.json();
      console.log("Delete API response:", result);

      if (result.status) {
        // Refresh from the server so all of the deleted client's sessions
        // (and any leftover rows) are removed from the list.
        setSelectedClientIds([]);
        await fetchSummaries();
        setToast({
          message:
            deleteClientIds.length > 1
              ? "Clients deleted successfully!"
              : "Client deleted successfully!",
          type: "success",
        });
      } else {
        setToast({
          message: `Failed to delete client(s): ${result.message || "Unknown error"}`,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      setToast({
        message: "An error occurred while deleting the client. Please try again.",
        type: "error",
      });
    } finally {
      // Clear the stored ID
      setDeleteClientIds([]);
    }
  };

  const cancelDelete = () => {
    setShowDeletePopup(false);
    setDeleteClientIds([]);
  };

  // Format Date to "DD MMM YYYY"
  const formatDateDisplay = (dateString) => {
    if (!dateString || dateString === "-" || dateString === "null" || dateString === "undefined") return "-";
    const raw = String(dateString).trim();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
      const y = match[1];
      const mIdx = parseInt(match[2], 10) - 1;
      const d = String(parseInt(match[3], 10)).padStart(2, "0");
      if (mIdx >= 0 && mIdx < 12) {
        return `${d} ${months[mIdx]} ${y}`;
      }
    }
    const match2 = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (match2) {
      const d = String(parseInt(match2[1], 10)).padStart(2, "0");
      const mIdx = parseInt(match2[2], 10) - 1;
      const y = match2[3];
      if (mIdx >= 0 && mIdx < 12) {
        return `${d} ${months[mIdx]} ${y}`;
      }
    }
    const date = new Date(raw);
    if (isNaN(date.getTime())) return dateString;
    const d = String(date.getDate()).padStart(2, "0");
    const m = months[date.getMonth()];
    const y = date.getFullYear();
    return `${d} ${m} ${y}`;
  };

  // -- Filtering Logic ---------------------------------------------

  const filteredClients = clients.filter((client) => {
    // Global Search - matches ANY column (safe for numbers/null)
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      String(client.id ?? "").toLowerCase().includes(q) ||
      String(client.clientId ?? "").toLowerCase().includes(q) ||
      String(client.name ?? "").toLowerCase().includes(q) ||
      String(client.assessmentDate ?? "").toLowerCase().includes(q) ||
      String(client.appointmentType ?? "").toLowerCase().includes(q) ||
      String(client.goalDomain ?? "").toLowerCase().includes(q) ||
      String(client.targetDate ?? "").toLowerCase().includes(q) ||
      String(client.status ?? "").toLowerCase().includes(q) ||
      String(client.achievedBy ?? "").toLowerCase().includes(q);

    const clientDate = new Date(client.assessmentDate);
    // Handle invalid dates gracefully
    if (isNaN(clientDate.getTime())) return false;

    // FIX: Set the time to the start of the day (00:00:00) so it captures all records for that day
    let from = new Date("1900-01-01");
    if (fromDate) {
      from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
    }

    // FIX: Set the time to the end of the day (23:59:59) so it captures all records for that day
    let to = new Date("2100-12-31");
    if (toDate) {
      to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
    }

    const matchesDateRange = clientDate >= from && clientDate <= to;
    const matchesFavourite = favoritesOnly ? Boolean(client.is_favourite) : true;
    return matchesSearch && matchesDateRange && matchesFavourite;
  });

  // -- Pagination Logic --------------------------------------------

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentClients = filteredClients.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, fromDate, toDate, favoritesOnly]);

  // -- Render --------------------------------------------------------

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <Link  onClick={() => navigate(-1)} className="back-btn2" aria-label="Go back to dashboard">
              <FaArrowLeft className="back-icon" aria-hidden="true" />
              Previous page
            </Link>

      <div className="continue-container">
        {/* Header */}
        <div className="common-page-header">
          <h1 className="common-page-title">Suggested Interventions</h1>
          <p className="common-page-subtitle">View the history of your client's suggested interventions here.</p>
        </div>

        {/* Search & Filter */}
        <div className="filter-section">
          <div className="search-box">
            <div className="tooltip" data-tooltip={favoritesOnly ? "Show all clients" : "Show favourites only"}>
              <button
                className={`starfav${favoritesOnly ? " active" : ""}`}
                onClick={() => setFavoritesOnly((prev) => !prev)}
                aria-pressed={favoritesOnly}
                aria-label={favoritesOnly ? "Show all clients" : "Show favourites only"}
              >
                <FaStar size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="common-search-box" style={{ marginLeft: "8px" }}>
              <input
                type="text"
                placeholder="Search"
                aria-label="Search summaries by client ID, name, date, appointment type, domain, or status"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="common-search-icon" aria-hidden="true" />
            </div>
          </div>

          <div className="filter-date-range">

            {/* From Date */}
            <div className="filter-date-wrapper">
              <label htmlFor="from-date" className="sr-only">
                From Date
              </label>

              <div
                className="filter-date-box"
                role="button"
                tabIndex="0"
                onClick={() => fromRef.current?.showPicker()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fromRef.current?.showPicker(); } }}
                aria-label={`Filter from date${fromDate ? `: ${fromDate}` : ""}`}
                aria-haspopup="dialog"
              >
                <span className={!fromDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {fromDate || "From"}
                </span>

                <FaCalendarAlt className="filter-date-icon" />

                <input
                  type="date"
                  id="from-date"
                  name="fromDate"
                  ref={fromRef}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="filter-date-input"
                  aria-label="From Date"
                />
                <div id="from-date-help" className="sr-only"> Select a date using the calendar or enter in format YYYY-MM-DD. </div>
              </div>
            </div>

            {/* To Date */}
            <div className="filter-date-wrapper">
              <label htmlFor="to-date" className="sr-only">
                To Date
              </label>

              <div
                className="filter-date-box"
                role="button"
                tabIndex="0"
                onClick={() => toRef.current?.showPicker()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toRef.current?.showPicker(); } }}
                aria-label={`Filter to date${toDate ? `: ${toDate}` : ""}`}
                aria-haspopup="dialog"
              >
                <span className={!toDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {toDate || "To"}
                </span>

                <FaCalendarAlt className="filter-date-icon" />

                <input
                  type="date"
                  id="to-date"
                  name="toDate"
                  ref={toRef}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="filter-date-input"
                  aria-label="To Date"
                />
                <div id="to-date-help" className="sr-only"> Select a date using the calendar or enter in format YYYY-MM-DD. </div>
              </div>
            </div>

          </div>
        </div>

        {/* Bulk delete toolbar */}
        {selectedClientIds.length > 0 && (
          <div className="bulk-delete-bar">
            <span className="bulk-delete-count">
              {selectedClientIds.length} client{selectedClientIds.length > 1 ? "s" : ""} selected
            </span>
            <button className="bulk-delete-btn" onClick={handleDeleteSelected}>
              <FaTrashAlt aria-hidden="true" /> Delete Selected
            </button>
          </div>
        )}

        {/* Table */}
        <div className="table-wrapper">
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              Loading summaries...
            </div>
          ) : (
            <table className="client-table" role="table" aria-label="Summaries table">
              <caption className="sr-only">Client assessments</caption>
              <colgroup>
                {/* <col className="col-select" /> */}
                <col className="col-id" />
                <col className="col-name" />
                <col className="col-date" />
                <col className="col-appt-type" />
                <col className="col-action" />
              </colgroup>

              <thead>
                <tr>
                  {/* <th scope="col" style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      aria-label="Select all clients on this page"
                      checked={allPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = somePageSelected && !allPageSelected;
                      }}
                      onChange={toggleSelectAllPage}
                    />
                  </th> */}
                  <th scope="col">Client ID</th>
                  <th scope="col">Client</th>
                  <th scope="col" style={{ textAlign: "center" }}>Assessment Date</th>
                  <th scope="col" style={{ textAlign: "center" }}>Session</th>
                  <th scope="col" style={{ textAlign: "center" }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {currentClients.map((c, index) => (
                  <tr key={`${c.clientId}-${c.sessionId}`} className={index % 2 === 1 ? "alt-row" : ""}>
                    {/* <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${c.name}`}
                        checked={selectedClientIds.includes(c.clientId)}
                        onChange={() => toggleClientSelection(c.clientId)}
                        disabled={!c.clientId}
                      />
                    </td> */}
                    <td className="td-id" title={c.id}>
                      {c.id}
                    </td>

                    <td className="td-name" title={c.name}>
                      {c.name}
                    </td>

                    <td style={{ textAlign: "center" }} className="td-date text-center">
                      {formatDateDisplay(c.assessmentDate)}
                    </td>

                    <td style={{ textAlign: "center" }} className="td-appt-type text-center">
                      {appointmentDisplayLabel(c.appointmentType)}
                    </td>

                    <td style={{ textAlign: "center" }} className="actions1 text-center">
                      <div className="icon-row">
                        <Link
                          to={`/Interventions?sessionId=${c.sessionId}${fromReview ? "&fromReview=1" : ""}`}
                          className="icon-wrapper"
                          aria-label={`View summary for ${c.name}`}
                          title={`View summary for ${c.name}`}
                          style={{ border: "none", background: "transparent", cursor: "pointer", textDecoration: "none" }}
                        >
                          <FaEye className="icon-btn view" aria-hidden="true" />
                        </Link>

                        <button
                          className="icon-wrapper"
                          onClick={() => {
                            if (!c.sessionId) {
                              alert("Error: Session ID is missing. Cannot update favourite.");
                              return;
                            }
                            handleToggleFavourite(c.sessionId);
                          }}
                          aria-label={c.is_favourite ? `Remove ${c.name} from favourites` : `Add ${c.name} to favourites`}
                          aria-pressed={c.is_favourite === true}
                          style={{ border: "none", background: "transparent", cursor: "pointer" }}
                          title={c.is_favourite ? "Remove from favourites" : "Add to favourites"}
                        >
                          <FaStar
                            className={`icon-btn fav${c.is_favourite === true ? " is-fav" : ""}`}
                            aria-hidden="true"
                          />
                        </button>

                        <button
                          className="icon-wrapper"
                          onClick={() => {
                            console.log("Delete button clicked for client:", c.clientId, c.name);
                            if (!c.clientId) {
                              alert("Error: Client ID is missing. Cannot delete.");
                              return;
                            }
                            handleDeleteClient(c.clientId);
                          }}
                          aria-label={`Delete ${c.name}`}
                          style={{ border: "none", background: "transparent", cursor: "pointer" }}
                          title={`Delete ${c.name}`}
                        >
                          <FaTrashAlt className="icon-btn delete" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && filteredClients.length === 0 && (
            <div className="no-results">
              <p>No found matching your favorite client.</p>
            </div>
          )}
        </div>

        {!loading && filteredClients.length > itemsPerPage && (
          <div className="pagination">
            <button
              aria-label="Previous page"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={currentPage === i + 1 ? "active" : ""}
                aria-label={`Page ${i + 1}`}
                aria-current={currentPage === i + 1 ? "page" : undefined}
              >
                {i + 1}
              </button>
            ))}

            <button
              aria-label="Next page"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
        {showDeletePopup && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-client-title"
            aria-describedby="delete-client-desc"
            style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '400px',
              textAlign: 'center',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <h3 id="delete-client-title" style={{ marginBottom: '15px', color: '#2b5601' }}>Delete Client{deleteClientIds.length > 1 ? "s" : ""}</h3>
              <p id="delete-client-desc" style={{ marginBottom: '25px', color: '#1c1d1b' }}>
                Are you sure you want to delete {deleteClientIds.length} client{deleteClientIds.length > 1 ? "s" : ""}?
                This will remove all of their sessions and goals.
              </p>
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                  onClick={cancelDelete}
                  aria-label="No, keep this client"
                  style={{
                    padding: '10px 20px',
                    border: '1px solid var(--cpa-gray-3, #737A7E)',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  No
                </button>
                <button
                  onClick={confirmDelete}
                  aria-label="Yes, delete this client"
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    backgroundColor: 'var(--cpa-error-red, #C22B27)',
                    color: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Summaries;
